import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, Calendar, Trash2, UserPlus, Users } from 'lucide-react';
import { adminUserService, turmaService } from '../../shared/services';
import { usePermissions } from '../../app/hooks/usePermissions';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

export function TurmaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [alunoId, setAlunoId] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const { data: turma, isLoading, isError } = useQuery({
    queryKey: ['turma', id],
    queryFn: () => turmaService.getTurma(id!),
    enabled: !!id,
    retry: false,
  });

  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios-turma'],
    queryFn: () => adminUserService.getUsers(),
    enabled: isAdmin,
  });

  const usuarios = useMemo(() => {
    const lista = Array.isArray(usuariosData) ? usuariosData : [];
    return lista.map((item: Record<string, unknown>) => ({
      id: String(item.id ?? item.Id ?? ''),
      nome: String(item.name ?? item.nome ?? item.Name ?? 'Sem nome'),
      email: String(item.email ?? item.Email ?? ''),
    }));
  }, [usuariosData]);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['turma', id] });
    queryClient.invalidateQueries({ queryKey: ['turmas'] });
  };

  const matricular = useMutation({
    mutationFn: () => turmaService.matricular(id!, alunoId),
    onSuccess: () => {
      setAlunoId('');
      setErro(null);
      invalidar();
    },
    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível matricular o aluno.')),
  });

  const desmatricular = useMutation({
    mutationFn: (aluno: string) => turmaService.desmatricular(id!, aluno),
    onSuccess: () => {
      setErro(null);
      invalidar();
    },
    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível remover a matrícula.')),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError || !turma) {
    return (
      <div className="p-8 max-w-3xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Turma não encontrada</h1>
        <Link to="/turmas" className="text-primary-600">Voltar às turmas</Link>
      </div>
    );
  }

  const alunos = turma.alunos ?? [];

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <Link to="/turmas" className="inline-flex items-center gap-2 text-primary-600 mb-6">
          <ArrowLeft size={16} /> Voltar às turmas
        </Link>

        <div className="card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{turma.nome}</h1>
              {turma.descricao && <p className="text-gray-600 mt-2">{turma.descricao}</p>}
              <p className="text-sm text-gray-500 mt-2">Instrutor: {turma.instructorName}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${turma.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {turma.ativo ? 'Ativa' : 'Encerrada'}
            </span>
          </div>

          <div className="flex flex-wrap gap-6 mt-5 text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <Calendar size={16} /> Início: {new Date(turma.dataInicio).toLocaleDateString('pt-BR')}
            </span>
            {turma.dataFim && (
              <span className="flex items-center gap-2">
                <Calendar size={16} /> Fim: {new Date(turma.dataFim).toLocaleDateString('pt-BR')}
              </span>
            )}
            <span className="flex items-center gap-2">
              <Users size={16} /> {turma.totalAlunos} aluno(s)
            </span>
          </div>
        </div>

        {erro && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

        {isAdmin && (
          <form
            className="card mt-6 flex flex-col sm:flex-row gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!alunoId) return;
              setErro(null);
              matricular.mutate();
            }}
          >
            <select
              value={alunoId}
              onChange={(event) => setAlunoId(event.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
              required
            >
              <option value="">Selecione o colaborador</option>
              {usuarios.map((usuario) => (
                <option key={usuario.id} value={usuario.id}>
                  {usuario.nome} — {usuario.email}
                </option>
              ))}
            </select>
            <button type="submit" disabled={matricular.isPending} className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60">
              <UserPlus size={16} /> {matricular.isPending ? 'Matriculando...' : 'Matricular'}
            </button>
          </form>
        )}

        <section className="card mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Alunos</h2>
          {alunos.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhum aluno matriculado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">Nome</th>
                    <th className="py-2 pr-4">E-mail</th>
                    <th className="py-2 pr-4">Matrícula em</th>
                    {isAdmin && <th className="py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {alunos.map((aluno) => (
                    <tr key={aluno.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium text-gray-900">{aluno.nome}</td>
                      <td className="py-2 pr-4 text-gray-600">{aluno.email}</td>
                      <td className="py-2 pr-4">{new Date(aluno.dataMatricula).toLocaleDateString('pt-BR')}</td>
                      {isAdmin && (
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Remover ${aluno.nome} da turma?`)) {
                                setErro(null);
                                desmatricular.mutate(aluno.alunoId);
                              }
                            }}
                            className="text-gray-400 hover:text-red-600"
                            title="Remover matrícula"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
